import { computed, ref, type ComputedRef, type InjectionKey } from 'vue';
import { IMenuType, type IMenuItem, NAV_MENU_TYPE, USER_PREFERENCES_MENU_TYPE } from './types';
import { useNavBarState } from './composables/use-navbar-store';
import { useHistoryDialog } from './composables/use-history-dialog';
import { TrackEventType, TrackSourceType } from '../../utils/track';
// ==================== 配置类型 ====================

/**
 * HTTP 客户端接口
 */
export interface IAxiosClient {
    get: <T = any>(config: { url: string; params?: any; [key: string]: any }) => Promise<T>;
    post: <T = any>(config: { url: string; data?: any; [key: string]: any }) => Promise<T>;
    delete: <T = any>(config: { url: string; data?: any; [key: string]: any }) => Promise<T>;
}

/**
 * 埋点参数接口
 */
export interface ITrackParams {
    /** 事件类型 */
    event_type: TrackEventType;
    /** 事件详情 */
    event_detail_str?: string;
    /** 模块类型 */
    module_type?: string;
    /** 来源类型 */
    source_type?: TrackSourceType;
    /** 其他参数 */
    other_param?: string;
}

/**
 * HeaderService 接口（简化版）
 */
export interface IHeaderService {
    userCenterService: {
        logged: boolean;
    };
    deferred: {
        promise: Promise<void>;
    };
    account?: {
        _info?: {
            incognito_flag?: boolean;
            company_id?: string;
        };
        _info_?: {
            incognito_flag?: boolean;
            company_id?: string;
        };
    };
    /** 白标模式 */
    whiteLabel?: boolean;
    /** 自定义 Logo 是否可见 */
    customLogoVisible?: boolean;
    /** 自定义 Logo URL */
    customLogoUrl?: string;
    /** Logo 跳转链接 */
    logoHref?: string;
    /** 登录回调 */
    login?: () => void;
}

export interface IIpNavCreditsConfig {
    /** 是否开启侧边栏积分入口 */
    enabled?: boolean;
    /** 通用积分页面 URL */
    creditsPageUrl?: string;
    /** Eureka API 基础 URL */
    apiBaseUrl?: string;
    /** Drafting API 基础 URL */
    draftingApiBaseUrl?: string;
}

export interface IDraftingPlanForCredits {
    billing_mode?: string;
    plan_type?: string;
    has_credits_package?: boolean;
}

export interface IPersonalCreditsDetail {
    total_credits: number;
    used_credits: number;
    daily_used_credits: number;
}

export interface ICompanyCreditsDetail extends IPersonalCreditsDetail {
    user_daily_limit: number;
    company_user_daily_limit: number;
    company_total_daily_limit: number;
}

export interface IRechargeInfo {
    has_recharge_records: boolean;
    is_personal_account: boolean;
    is_latest_recharge_over_30_days: boolean;
    has_pay_packages?: boolean;
}

/**
 * 菜单配置项
 */
export interface IMenuConfig {
    type: string;
    icon?: string;
    link?: string;
    router?: string;
    module?: string;
    children?: IMenuConfig[];
    groupFirst?: boolean;
    needLogin?: boolean;
}

/**
/**
 * 导航回调参数
 */
export interface INavigateContext {
    /** 被点击的菜单项 */
    menu: INavMenuItem;
    /** 设置高亮状态 */
    setActive: (active: boolean) => void;
    /** 是否阻止默认跳转行为 */
    preventDefault: () => void;
}

/**
 * 路由变化时的高亮检查上下文
 */
export interface ICheckActiveContext {
    /** 菜单列表（整个菜单配置） */
    menus: INavMenuItem[];
    /** 当前路由路径 */
    routePath: string;
    /** 当前路由的全部信息 */
    route: Record<string, any>;
}

/**
 * 路由变化时检查高亮的回调
 * @param context 包含菜单列表和路由信息
 * @returns 应该高亮的菜单项（type），不高亮返回 null
 */
export type IsActiveCallback = (context: ICheckActiveContext) => string | null;

/**
 * IpNavService 配置
 */
export interface IIpNavConfig {
    /** HeaderService 实例 */
    headerService: IHeaderService;
    /** HTTP 客户端实例 */
    httpClient: IAxiosClient;
    /** API 基础 URL（默认使用环境变量 VITE_EUREKA_SERVICE_URI） */
    apiBaseUrl?: string;
    /** 自定义菜单配置（可选） */
    menuConfig?: IMenuConfig[];
    /** 主题 */
    theme?: 'light' | 'dark';
    /** 是否启用响应式布局 */
    responsive?: boolean;
    /** 路由变化时检查高亮的回调（返回菜单的 type，不高亮返回 null） */
    isActive?: IsActiveCallback;
    /** 菜单点击回调 */
    onNavigate?: (context: INavigateContext) => void;
    /** 升级按钮点击回调 */
    onUpgrade?: () => void;
    /** 登录按钮点击回调 */
    onLogin?: () => void;
    /** 是否显示升级按钮（可选，支持外部复写） */
    isShowUpgrade?: () => boolean;
    /** 侧边栏积分入口配置 */
    creditsConfig?: IIpNavCreditsConfig;
}

// ==================== 扩展类型 ====================

/**
 * 带路由和链接的菜单项（用于导航）
 */
export interface INavMenuItem extends IMenuItem {
    link?: string;
    router?: string;
}

// ==================== 组件注入类型 ====================

export interface IIpNavContext {
    /** 展开状态 */
    opened: { value: boolean };
    /** 菜单列表 */
    menus: ComputedRef<INavMenuItem[]>;
    /** 历史记录菜单 */
    navHistory: ComputedRef<INavMenuItem>;
    /** 历史记录列表（拍平后） */
    historyList: ComputedRef<INavMenuItem[]>;
    /** 原始分组历史数据 */
    historyMaps: ComputedRef<{ date: string; data: INavMenuItem[] }[]>;
    /** 国际化文案 */
    messages: Record<string, any>;
    /** 主题 */
    theme: string;
    /** 是否启用响应式布局 */
    responsive: boolean;
    /** 侧边栏宽度配置 */
    navWidth: number;
    navMiniWidth: number;
    /** 跳转到菜单 */
    navigateTo: (menu: INavMenuItem) => void;
    /** 自定义导航回调 */
    onNavigate?: (context: INavigateContext) => void;
    /** 删除历史记录 */
    removeHistory: (item: INavMenuItem) => void;
    /** 删除所有历史 */
    removeAllHistory: () => void;
    /** 点击历史记录 */
    clickHistory: (item: INavMenuItem) => void;
    /** 登录状态 */
    isLogin: ComputedRef<boolean>;
    /** 无痕模式可见 */
    incognitoVisible: ComputedRef<boolean>;
    /** 切换侧边栏 */
    toggleSidebar: () => void;
    /** 设置高亮菜单类型 */
    setActiveMenuType: (type: NAV_MENU_TYPE | string | null) => void;
    /** 清除高亮 */
    clearActiveMenuType: () => void;
    /** 获取用户套餐类型 */
    getUserPackageType: () => Promise<Array<{ eureka_package: string }>>;
}

/**
 * 注入 Key
 */
export const IpNavServiceKey: InjectionKey<IIpNavContext> = Symbol('IpNavService');

// ==================== 默认配置 ====================

const DEFAULT_CONFIG: IIpNavConfig = {
    headerService: {} as IHeaderService,
    httpClient: {} as IAxiosClient,
    apiBaseUrl: typeof import.meta !== 'undefined' && import.meta.env?.VITE_EUREKA_SERVICE_URI
        ? import.meta.env.VITE_EUREKA_SERVICE_URI
        : 'https://qa-eureka-service.zhihuiya.com',
    menuConfig: [],
    theme: 'dark',
    responsive: true,
};

// ==================== 默认菜单配置 ====================

export interface IMenuItemConfig extends IMenuConfig {
    authKey?: string;
    expand?: boolean;
}

// NEW_TASKS = 'NEW_TASKS',
// SEARCH_TASKS = 'SEARCH_TASKS',
// KNOWLEDGE_BASE = 'KNOWLEDGE_BASE',
// MONITOR = 'MONITOR',
// COMMUNITY = 'COMMUNITY'

const DEFAULT_MENU_CONFIG: IMenuItemConfig[] = [
    {
        type: NAV_MENU_TYPE.NEW_TASKS,
        icon: 'MenuNewTask',
        link: '/ip/#/home',
        module: NAV_MENU_TYPE.NEW_TASKS,
    },
    {
        type: NAV_MENU_TYPE.SEARCH_TASKS,
        icon: 'SolidSearchLeft',
        module: NAV_MENU_TYPE.SEARCH_TASKS,
        needLogin: true,
    },
    {
        type: NAV_MENU_TYPE.AGENT,
        icon: 'MenuAgent',
        link: '/agent-list',
        module: NAV_MENU_TYPE.AGENT,
    },
    {
        type: NAV_MENU_TYPE.CUSTOMIZE,
        icon: 'MenuCustomize',
        link: '/customize-list',
        module: NAV_MENU_TYPE.CUSTOMIZE,
    },

    {
        type: NAV_MENU_TYPE.MONITOR,
        icon: 'SolidAim',
        link: '/monitor/#/my?from=invite-monitor-nav',
        module: NAV_MENU_TYPE.MONITOR,
    },
    {
        type: NAV_MENU_TYPE.KNOWLEDGE_BASE,
        icon: 'MenuVault',
        link: '/vault',
        module: NAV_MENU_TYPE.KNOWLEDGE_BASE,
    },
    {
        type: NAV_MENU_TYPE.COMMUNITY,
        icon: 'MenuCommunity',
        link: '/rd-gallery',
        module: NAV_MENU_TYPE.COMMUNITY,
    },

    {
        type: NAV_MENU_TYPE.DRAFTING,
        link: '/DRAFTING',
        icon: 'IconTask',
        module: NAV_MENU_TYPE.DRAFTING,
        expand: true,
        children: [
            {
                type: 'DraftingCenter',
                link: '/ip/drafting/#/patent-drafting/projects',
            }, {
                type: 'Templates',
                link: '/ip/drafting/#/templates',
            }, {
                type: 'AIInstructions',
                link: '/ip/drafting/#/instructions',
            }, {
                type: USER_PREFERENCES_MENU_TYPE,
            },
            // WritingStyles is intentionally hidden while the legacy writing-styles module is retained.
            // {
            //     type: 'WritingStyles',
            //     link: '/ip/drafting/#/writing-styles',
            // },
        ],
    },
];

// ==================== 全局高亮回调引用 ====================

// ==================== IpNavService 类 ====================

export class IpNavService {
    private config: IIpNavConfig;

    /** 状态管理实例 */
    private readonly state = useNavBarState();

    /** 使用全局的展开状态 */
    public readonly opened = this.state.opened;

    /** 历史记录列表（拍平后，供侧边栏使用） */
    public readonly historyList = ref<INavMenuItem[]>([]);

    /** 原始分组历史数据（供 Dialog 使用） */
    public readonly historyMaps = ref<{ date: string; data: INavMenuItem[] }[]>([]);

    /** HeaderService 实例（暴露给外部使用） */
    public readonly headerService: IHeaderService;

    /** 权限控制 */
    // public readonly control = ref<IControl>();

    /** 是否登录 */
    public readonly isLogin = computed(() => {
        return this.headerService.userCenterService?.logged ?? false;
    });

    /** 是否显示升级按钮 */
    public readonly isShowUpgrade = computed(() => {
        // 如果外部提供了 isShowUpgrade 回调，使用外部的判断逻辑
        if (this.config.isShowUpgrade) {
            return this.config.isShowUpgrade();
        }
        // 默认返回 true（显示升级按钮）
        return true;
    });

    /** 无痕模式可见 */
    public readonly incognitoVisible = computed(() => {
        return this.headerService.account?._info?.incognito_flag ?? false;
    });

    /** 是否启用响应式布局 */
    public readonly responsive: boolean;

    constructor(config: IIpNavConfig) {
        this.config = { ...DEFAULT_CONFIG, ...config } as Required<IIpNavConfig>;
        this.headerService = this.config.headerService;
        this.responsive = this.config.responsive ?? true;

        // 初始化
        this.init();
    }

    /**
     * 初始化（可被子类覆写）
     */
    protected async init() {
        // 等待权限控制准备好
        // this.control.value = await controlDeferred.promise;
        // 历史记录由外部调用 queryHistoryList() 加载
    }

    /**
     * 获取菜单配置（可被子类覆写）
     */
    protected getMenuConfig(): IMenuItemConfig[] {
        return this.config.menuConfig?.length ? this.config.menuConfig as IMenuItemConfig[] : DEFAULT_MENU_CONFIG;
    }

    /**
     * 获取菜单列表
     */
    public getMenus(): INavMenuItem[] {
        const menuConfig = this.getMenuConfig();
        return menuConfig
            // .filter(item => {
            //     // 权限检查
            //     if (item.authKey) {
            //         return (this.control.value as any)?.[item.authKey];
            //     }
            //     return true;
            // })
            .map(item => ({
                id: item.type,
                type: item.type as IMenuType,
                title: item.type,
                icon: item.icon,
                link: item.link,
                router: item.router,
                module: item.module,
                active: false,
                children: item.children?.map(child => ({
                    id: child.type,
                    type: child.type as IMenuType,
                    title: child.type,
                    icon: child.icon,
                    link: child.link,
                    router: child.router,
                    module: child.module,
                    active: false,
                })),
            }));
    }

    /**
     * 获取历史记录菜单
     */
    public getNavHistory(): INavMenuItem {
        return {
            id: 'history',
            type: IMenuType.TECH_RESEARCH,
            icon: 'SolidTimeRefresh',
            title: 'HISTORY',
            children: this.historyList.value,
        };
    }

    /**
     * 查询历史记录
     */
    public async queryHistoryList() {
        try {
            const res = await this.config.httpClient.get({
                url: `${this.config.apiBaseUrl}/eureka/history/records`,
                params: { page: 1, pageSize: 100, type: 'playbook', duration: 1 },
            });

            const historyMaps = res.data?.history_maps || [];

            // 保存原始分组数据（供 Dialog 使用）
            this.historyMaps.value = historyMaps.map((group: any) => ({
                date: group.date,
                data: group.data.map((item: any) => ({
                    id: item.id,
                    type: item.source as IMenuType,
                    title: item.message,
                    link: item.link,
                    router: undefined,
                    icon_url: item.icon_url,
                    business_name: item.business_name,
                    context_type: item.context_type,
                    active: false,
                })),
            }));

            // 拍平数据（供侧边栏使用）
            this.historyList.value = historyMaps.flatMap((group: any) =>
                group.data.map((item: any) => ({
                    id: item.id,
                    type: item.source as IMenuType,
                    title: item.message,
                    link: item.link,
                    router: undefined,
                    icon_url: item.icon_url,
                    business_name: item.business_name,
                    active: false,
                })),
            );
        } catch (error) {
            console.warn('[IpNavService] Failed to query history:', error);
        }
    }

    /**
     * 删除单条历史记录
     */
    public async removeHistory(item: INavMenuItem) {
        if (!item.id) return;

        try {
            await this.config.httpClient.delete({
                url: `${this.config.apiBaseUrl}/eureka/history`,
                data: { type: 'playbook', history_ids: [item.id] },
            });

            // API 调用成功后，从列表中移除
            this.historyList.value = this.historyList.value.filter(h => h.id !== item.id);
        } catch (error) {
            console.warn('[IpNavService] Failed to remove history:', error);
            throw error;
        }
    }

    /**
     * 删除所有历史记录
     */
    public async removeAllHistory() {
        try {
            await this.config.httpClient.delete({
                url: `${this.config.apiBaseUrl}/eureka/history`,
                data: { type: 'all' },
            });

            // API 调用成功后，清空列表
            this.historyList.value = [];
        } catch (error) {
            console.warn('[IpNavService] Failed to remove all history:', error);
            throw error;
        }
    }

    /**
     * 跳转到菜单（可通过配置 onNavigate 覆写，也可直接调用）
     */
    public navigateTo(menu: INavMenuItem): void {
        let shouldNavigate = true;
        let shouldSetActive = true;

        // 埋点上报
        if (menu.type) {
            this.track({
                event_type: TrackEventType.IpNavClick,
                module_type: 'EUREKA',
                source_type: TrackSourceType.IpNav,
                other_param: menu.type,
            });
        }

        // 如果外部传入了 onNavigate 回调，优先使用
        if (this.config.onNavigate) {
            const context: INavigateContext = {
                menu,
                setActive: (active: boolean) => {
                    shouldSetActive = active;
                    if (active) {
                        this.setActiveMenuType(menu.type);
                    } else {
                        this.clearActiveMenuType();
                    }
                },
                preventDefault: () => {
                    shouldNavigate = false;
                },
            };
            this.config.onNavigate(context);

            // 如果阻止了跳转，直接返回
            if (!shouldNavigate) {
                return;
            }

            // 如果设置了高亮状态
            if (shouldSetActive) {
                this.setActiveMenuType(menu.type);
            }
        } else {
            // 默认高亮当前菜单
            this.setActiveMenuType(menu.type);
        }

        // 专门处理打开弹窗的菜单
        if (menu.type === NAV_MENU_TYPE.SEARCH_TASKS as any) {
            const { open } = useHistoryDialog();
            open();
            // 触发打开历史记录 dialog 的事件
            // 由外部组件监听并处理
            return;
        }

        // 默认跳转逻辑
        if (menu.router) {
            window.location.href = menu.router;
        } else if (menu.link) {
            const isExternal = /^https?:\/\//.test(menu.link);
            window.open(menu.link, isExternal ? '_blank' : '_self');
        }
    }

    /**
     * 点击历史记录
     */
    public clickHistory(item: INavMenuItem) {
        this.navigateTo(item);
    }

    /**
     * 设置侧边栏展开/收起
     * @param value true-展开，false-收起，不传则切换当前状态
     */
    public toggleSidebar(value?: boolean): void {
        if (value !== undefined) {
            this.opened.value = value;
        } else {
            this.opened.value = !this.opened.value;
        }
    }

    /**
     * 设置当前高亮的菜单类型
     * @param type 菜单类型
     * @param id 菜单ID（可选，用于历史记录）
     */
    public setActiveMenuType(type: NAV_MENU_TYPE | string | null, id?: string | null): void {
        const { setActiveMenuType } = useNavBarState();
        setActiveMenuType(type, id);
    }

    /**
     * 清除高亮状态
     */
    public clearActiveMenuType(): void {
        const { clearActiveMenuType } = useNavBarState();
        clearActiveMenuType();
    }

    /**
     * 根据路由检查高亮（供组件内部调用）
     * 组件应该在路由变化时调用此方法
     * @param menus 当前菜单列表
     * @param route 当前路由信息
     */
    public isActiveFromRoute(menus: INavMenuItem[], route: Record<string, any>): void {
        if (this.config.isActive) {
            const result = this.config.isActive({
                menus,
                routePath: route.path || '',
                route,
            });
            if (result === null) {
                this.clearActiveMenuType();
            } else if (result !== undefined) {
                this.setActiveMenuType(result);
            }
        }
    }

    /**
     * 获取用户套餐类型
     */
    public async getUserPackageType(): Promise<Array<{ eureka_package: string }>> {
        try {
            const res = await this.config.httpClient.get({
                url: `${this.config.apiBaseUrl}/eureka/package-info`,
            });
            return res.data || [];
        } catch (error) {
            console.warn('[IpNavService] Failed to get user package type:', error);
            return [];
        }
    }

    public getCreditsConfig(): IIpNavCreditsConfig | undefined {
        return this.config.creditsConfig;
    }

    public async waitHeaderReady(): Promise<void> {
        await this.config.headerService.deferred?.promise;
    }

    public isCompanyUser(): boolean {
        const account = this.config.headerService.account as any;
        const info = account?._info || account?._info_;
        return !!info?.company_id;
    }

    public async getDraftingPlanForCredits(): Promise<IDraftingPlanForCredits | null> {
        const creditsConfig = this.config.creditsConfig;
        if (!creditsConfig?.enabled || !creditsConfig.draftingApiBaseUrl) {
            return null;
        }

        try {
            const res = await this.config.httpClient.get({
                url: `${creditsConfig.draftingApiBaseUrl}/api/user/drafting-plan`,
            });
            return res.data?.data || res.data || res;
        } catch (error) {
            console.warn('[IpNavService] Failed to get drafting plan for credits:', error);
            return null;
        }
    }

    public async getPersonalCreditsDetail(): Promise<IPersonalCreditsDetail | null> {
        const apiBaseUrl = this.config.creditsConfig?.apiBaseUrl || this.config.apiBaseUrl;
        if (!apiBaseUrl) return null;

        try {
            const res = await this.config.httpClient.get({
                url: `${apiBaseUrl}/api/eureka/credits/personal/detail`,
            });
            return res.data || res;
        } catch (error) {
            console.warn('[IpNavService] Failed to get personal credits detail:', error);
            return null;
        }
    }

    public async getCompanyCreditsDetail(): Promise<ICompanyCreditsDetail | null> {
        const apiBaseUrl = this.config.creditsConfig?.apiBaseUrl || this.config.apiBaseUrl;
        if (!apiBaseUrl) return null;

        try {
            const res = await this.config.httpClient.get({
                url: `${apiBaseUrl}/api/eureka/credits/company/detail`,
            });
            return res.data || res;
        } catch (error) {
            console.warn('[IpNavService] Failed to get company credits detail:', error);
            return null;
        }
    }

    public async getRechargeInfo(): Promise<IRechargeInfo | null> {
        const apiBaseUrl = this.config.creditsConfig?.apiBaseUrl || this.config.apiBaseUrl;
        if (!apiBaseUrl) return null;

        try {
            const res = await this.config.httpClient.get({
                url: `${apiBaseUrl}/api/eureka/credits/personal/recharge-info`,
            });
            return res.data || res;
        } catch (error) {
            console.warn('[IpNavService] Failed to get recharge info:', error);
            return null;
        }
    }

    /**
     * 埋点上报
     * @param params 埋点参数
     */
    public async track(params: ITrackParams): Promise<void> {
        try {
            await this.config.httpClient.post({
                url: `${this.config.apiBaseUrl}/eureka/tracking/add`,
                data: params,
            });
        } catch (error) {
            console.warn('[IpNavService] Failed to track:', error);
        }
    }
}

// ==================== 工厂函数 ====================

export function createIpNavService(config: IIpNavConfig): IpNavService {
    return new IpNavService(config);
}
